import { useMemo, useState } from "react";
import { IconEye, IconEyeOff, IconLock, IconPassword } from "@tabler/icons-react";
import { getIn, useFormik } from "formik";
import { Link, useSearchParams } from "react-router-dom";
import * as yup from "yup";
import InlineError from "@components/forms/InlineError";
import useResetPassword from "@hooks/auth/useResetPassword";
import authBackgroundImage from "@assets/img/auth/bg-auth.webp";
import brandLogo from "@assets/img/brand/logo.png";

const resetPasswordValidationSchema = yup.object({
  token: yup.string().trim().required("Token is required."),
  newPassword: yup
    .string()
    .required("Password is required.")
    .min(8, "Password must be at least 8 characters.")
    .matches(/[A-Z]/, "Password must include at least 1 uppercase letter.")
    .matches(/\d/, "Password must include at least 1 number.")
    .matches(
      /[^A-Za-z0-9]/,
      "Password must include at least 1 special character.",
    ),
  confirmPassword: yup
    .string()
    .required("Confirm password is required.")
    .oneOf([yup.ref("newPassword")], "Passwords must match."),
});

function maskToken(token = "") {
  const safeToken = String(token || "").trim();
  if (!safeToken) return "";
  if (safeToken.length <= 8) return safeToken;
  return `${safeToken.slice(0, 4)}${"•".repeat(
    Math.max(0, safeToken.length - 8),
  )}${safeToken.slice(-4)}`;
}

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const initialToken = useMemo(
    () => String(searchParams.get("token") || "").trim(),
    [searchParams],
  );
  const [showPassword, setShowPassword] = useState(false);
  const { resetPassword, isSubmitting, error, success } = useResetPassword();
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      token: initialToken,
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: resetPasswordValidationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      await resetPassword(values.token, values.newPassword);
    },
  });

  function getFieldError(path) {
    const touched = getIn(formik.touched, path);
    const fieldError = getIn(formik.errors, path);
    return touched ? fieldError || "" : "";
  }

  async function handleSubmit(event) {
    event.preventDefault();
    formik.setTouched(
      {
        token: true,
        newPassword: true,
        confirmPassword: true,
      },
      true,
    );
    await formik.submitForm();
  }

  return (
    <main
      className="auth-page auth-page--login"
      style={{ backgroundImage: `url(${authBackgroundImage})` }}
    >
      <section className="auth-card">
        <div className="auth-brand">
          <img className="auth-brand-logo" src={brandLogo} alt="Exploor CMS" />
        </div>
        <h1 className="auth-title">Reset Password</h1>
        <p className="auth-subtitle">Enter your reset token and create a new password.</p>
        <form className="form-grid auth-form-grid" onSubmit={handleSubmit}>
          <label className="field-label" htmlFor="token">
            <span className="auth-field-label">
              Token
              {getFieldError("token") ? (
                <InlineError>{getFieldError("token")}</InlineError>
              ) : null}
            </span>
            <span className="field-label app-form-field auth-form-field">
              <span className="auth-input-icon" aria-hidden="true">
                <IconPassword size={18} />
              </span>
              <input
                className="field-input"
                id="token"
                name="token"
                type="text"
                autoComplete="off"
                value={maskToken(formik.values.token)}
                readOnly
                disabled
                placeholder="Token not available"
              />
            </span>
          </label>

          <label className="field-label" htmlFor="newPassword">
            <span className="auth-field-label">
              New password
              {getFieldError("newPassword") ? (
                <InlineError>{getFieldError("newPassword")}</InlineError>
              ) : null}
            </span>
            <span className="field-label app-form-field auth-form-field">
              <span className="auth-input-icon" aria-hidden="true">
                <IconLock size={18} />
              </span>
              <input
                className="field-input auth-password-input"
                id="newPassword"
                name="newPassword"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Create your new password"
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <IconEyeOff size={18} /> : <IconEye size={18} />}
              </button>
            </span>
          </label>

          <label className="field-label" htmlFor="confirmPassword">
            <span className="auth-field-label">
              Confirm password
              {getFieldError("confirmPassword") ? (
                <InlineError>{getFieldError("confirmPassword")}</InlineError>
              ) : null}
            </span>
            <span className="field-label app-form-field auth-form-field">
              <span className="auth-input-icon" aria-hidden="true">
                <IconLock size={18} />
              </span>
              <input
                className="field-input auth-password-input"
                id="confirmPassword"
                name="confirmPassword"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Repeat your new password"
              />
            </span>
          </label>

          {error ? <p className="message-error">{error}</p> : null}
          {success ? <p className="message-ok">{success}</p> : null}
          <button
            className="button-primary auth-submit-btn"
            type="submit"
            disabled={isSubmitting || !formik.isValid}
          >
            {isSubmitting ? "Saving..." : "Update password"}
          </button>
        </form>
        <div className="auth-links">
          <Link to="/login">Back to sign in</Link>
        </div>
      </section>
    </main>
  );
}

export default ResetPasswordPage;
